import React, { useState, useEffect } from 'react';
import { PresentationTemplate, ProjectInfo } from '../../types';
import { fetchTemplates, deleteTemplate, deleteMasterTemplate } from '../../services/presentationService';

interface PresentationListModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectInfo: ProjectInfo;
    onViewPresentation: (template: PresentationTemplate) => void;
    onEditPresentation: (template: PresentationTemplate) => void;
}

const CATEGORY_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; icon: string }> = {
    semanal: {
        label: 'Planificación Semanal',
        bg: 'bg-blue-50 dark:bg-blue-950/40',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'calendar_view_week'
    },
    intermedia: {
        label: 'Planificación Intermedia (Lookahead)',
        bg: 'bg-emerald-50 dark:bg-emerald-950/40',
        text: 'text-emerald-700 dark:text-emerald-300',
        border: 'border-emerald-200 dark:border-emerald-800',
        icon: 'insights'
    },
    sgi: {
        label: 'Reunión SGI (Calidad y SSOMA)',
        bg: 'bg-amber-50 dark:bg-amber-950/40',
        text: 'text-amber-700 dark:text-amber-300',
        border: 'border-amber-200 dark:border-amber-800',
        icon: 'verified_user'
    },
    gerencial: {
        label: 'Reunión de Gerencia',
        bg: 'bg-purple-50 dark:bg-purple-950/40',
        text: 'text-purple-700 dark:text-purple-300',
        border: 'border-purple-200 dark:border-purple-800',
        icon: 'corporate_fare'
    },
    calidad: {
        label: 'Aseguramiento de Calidad',
        bg: 'bg-teal-50 dark:bg-teal-950/40',
        text: 'text-teal-700 dark:text-teal-300',
        border: 'border-teal-200 dark:border-teal-800',
        icon: 'verified'
    },
    general: {
        label: 'General',
        bg: 'bg-slate-50 dark:bg-slate-800',
        text: 'text-slate-700 dark:text-slate-300',
        border: 'border-slate-200 dark:border-slate-700',
        icon: 'slideshow'
    },
    unassigned: {
        label: 'Sin Módulo Asignado',
        bg: 'bg-slate-100 dark:bg-slate-800/60',
        text: 'text-slate-600 dark:text-slate-400',
        border: 'border-slate-200 dark:border-slate-700',
        icon: 'folder_off'
    }
};

export const PresentationListModal: React.FC<PresentationListModalProps> = ({
    isOpen,
    onClose,
    projectInfo,
    onViewPresentation,
    onEditPresentation
}) => {
    const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterCategory, setFilterCategory] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const loadData = async () => {
        if (!isOpen) return;
        setLoading(true);
        try {
            const data = await fetchTemplates(projectInfo?.id || '');
            setTemplates(data);
        } catch (e) {
            console.error('Error cargando lista de presentaciones:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [isOpen, projectInfo?.id]);

    if (!isOpen) return null;

    const filteredTemplates = templates.filter(t => {
        const cat = t.category || 'unassigned';
        const matchesCategory = filterCategory === 'all' || cat === filterCategory;
        const matchesQuery = !searchQuery.trim() || 
            t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesQuery;
    });

    const handleDelete = async (template: PresentationTemplate) => {
        const isMaster = template.projectId === 'global';
        const confirmMsg = isMaster 
            ? `¿Está seguro de eliminar la plantilla maestra "${template.name}"? Esta acción no se puede deshacer.`
            : `¿Está seguro de eliminar la presentación "${template.name}" de esta obra?`;

        if (!window.confirm(confirmMsg)) return;

        setDeletingId(template.id);
        try {
            if (isMaster) {
                await deleteMasterTemplate(template.id);
            } else {
                await deleteTemplate(template.id, projectInfo?.id);
            }
            await loadData();
        } catch (err) {
            console.error('Error al eliminar presentación:', err);
            alert('Ocurrió un error al intentar eliminar la presentación.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-150">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3">
                        <div className="size-10 rounded-xl bg-blue-500/10 dark:bg-blue-400/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[24px]">list_alt</span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                Lista de Presentaciones
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Catálogo de presentaciones configuradas y sus módulos de asignación para la obra
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        title="Cerrar ventana"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Filters & Search Toolbar */}
                <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                        <div className="relative flex-1">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">
                                search
                            </span>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Buscar por nombre o descripción..."
                                className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Módulo:</label>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                            <option value="all">Todos los Módulos</option>
                            <option value="semanal">Planificación Semanal</option>
                            <option value="intermedia">Planificación Intermedia</option>
                            <option value="sgi">Reunión SGI</option>
                            <option value="gerencial">Reunión de Gerencia</option>
                            <option value="calidad">Aseguramiento Calidad</option>
                            <option value="unassigned">Sin Módulo Asignado</option>
                        </select>
                        <button
                            onClick={loadData}
                            className="p-1.5 text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            title="Actualizar listado"
                        >
                            <span className="material-symbols-outlined text-[18px]">refresh</span>
                        </button>
                    </div>
                </div>

                {/* Table Content */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="py-20 flex flex-col items-center justify-center text-slate-400">
                            <span className="material-symbols-outlined animate-spin text-[32px] text-blue-500">progress_activity</span>
                            <span className="text-xs mt-3 font-medium">Cargando presentaciones...</span>
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="py-20 flex flex-col items-center justify-center text-slate-400 px-4 text-center">
                            <div className="size-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                                <span className="material-symbols-outlined text-[32px] text-slate-400">slideshow</span>
                            </div>
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">No se encontraron presentaciones</h3>
                            <p className="text-xs text-slate-500 mt-1 max-w-sm">
                                No existen presentaciones que coincidan con los filtros aplicados. Puedes guardar una nueva desde "Conf. Presentación".
                            </p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse text-xs">
                            <thead className="sticky top-0 bg-slate-100/90 dark:bg-slate-800/90 backdrop-blur-xs border-b border-slate-200 dark:border-slate-700 z-10">
                                <tr>
                                    <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300">Nombre de la Presentación</th>
                                    <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Módulo Asignado</th>
                                    <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">Diapositivas</th>
                                    <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Última Modificación</th>
                                    <th className="px-5 py-3 font-semibold text-slate-600 dark:text-slate-300 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filteredTemplates.map(tmpl => {
                                    const catInfo = CATEGORY_CONFIG[tmpl.category || 'unassigned'] || CATEGORY_CONFIG.unassigned;
                                    const isMaster = tmpl.projectId === 'global';
                                    const updatedStr = tmpl.updatedAt 
                                        ? new Date(tmpl.updatedAt).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                        : '—';

                                    return (
                                        <tr 
                                            key={tmpl.id}
                                            className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors group"
                                        >
                                            {/* Nombre */}
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="size-8 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 border border-blue-100 dark:border-blue-900/50">
                                                        <span className="material-symbols-outlined text-[18px]">co_present</span>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-slate-800 dark:text-slate-100 truncate">
                                                                {tmpl.name}
                                                            </span>
                                                            {isMaster && (
                                                                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                                                                    Plantilla Maestra
                                                                </span>
                                                            )}
                                                        </div>
                                                        {tmpl.description && (
                                                            <p className="text-[11px] text-slate-400 truncate max-w-md mt-0.5">
                                                                {tmpl.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Módulo asignado */}
                                            <td className="px-4 py-3.5">
                                                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${catInfo.bg} ${catInfo.text} ${catInfo.border}`}>
                                                    <span className="material-symbols-outlined text-[13px]">{catInfo.icon}</span>
                                                    <span>{catInfo.label}</span>
                                                </div>
                                            </td>

                                            {/* Diapositivas */}
                                            <td className="px-4 py-3.5 text-center">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold font-mono text-[11px]">
                                                    <span className="material-symbols-outlined text-[13px]">layers</span>
                                                    {tmpl.slides?.length || 1}
                                                </span>
                                            </td>

                                            {/* Fecha */}
                                            <td className="px-4 py-3.5 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                                                {updatedStr}
                                            </td>

                                            {/* Acciones */}
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {/* Ver */}
                                                    <button
                                                        onClick={() => {
                                                            onClose();
                                                            onViewPresentation(tmpl);
                                                        }}
                                                        className="px-2.5 py-1 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/50 font-semibold text-xs flex items-center gap-1 border border-blue-200 dark:border-blue-900/60 transition-all active:scale-95 cursor-pointer"
                                                        title="Ver presentación"
                                                    >
                                                        <span className="material-symbols-outlined text-[15px]">visibility</span>
                                                        <span>Ver</span>
                                                    </button>

                                                    {/* Editar */}
                                                    <button
                                                        onClick={() => {
                                                            onClose();
                                                            onEditPresentation(tmpl);
                                                        }}
                                                        className="px-2.5 py-1 rounded-lg text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 font-semibold text-xs flex items-center gap-1 border border-emerald-200 dark:border-emerald-900/60 transition-all active:scale-95 cursor-pointer"
                                                        title="Editar presentación"
                                                    >
                                                        <span className="material-symbols-outlined text-[15px]">edit</span>
                                                        <span>Editar</span>
                                                    </button>

                                                    {/* Eliminar */}
                                                    <button
                                                        onClick={() => handleDelete(tmpl)}
                                                        disabled={deletingId === tmpl.id}
                                                        className="p-1 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors disabled:opacity-40 cursor-pointer"
                                                        title="Eliminar presentación"
                                                    >
                                                        <span className="material-symbols-outlined text-[17px]">
                                                            {deletingId === tmpl.id ? 'sync' : 'delete'}
                                                        </span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                        Total de presentaciones: <strong className="text-slate-800 dark:text-slate-200">{filteredTemplates.length}</strong>
                    </span>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
